import type { User } from '../types';

export const NO_PERMISSIONS_MESSAGE = 'No permissions have been assigned to your role. Please contact your administrator or HR for assistance.';
export const NO_PERMISSIONS_ROUTE = '/no-permissions';
export const FORBIDDEN_ROUTE = '/forbidden';

export const normalizeRole = (role: string) => role.toLowerCase().replace(/[\s_-]/g, '');

export const FEATURE_KEYS = {
  DASHBOARD: 'DASHBOARD',
  REPORTS: 'REPORTS',
  EMPLOYEES: 'EMPLOYEES',
  ATTENDANCE: 'ATTENDANCE',
  LEAVE: 'LEAVE',
  ACTIVITY_LOGS: 'ACTIVITY_LOGS',
  LOCATION: 'LOCATION',
  GEO_LOCATION: 'GEO_LOCATION',
  BILLING: 'BILLING',
  NOTIFICATIONS: 'NOTIFICATIONS',
  PAYROLL: 'PAYROLL',
  BACKGROUND_VERIFICATION: 'BACKGROUND_VERIFICATION',
  SETTINGS: 'SETTINGS',
  TOOLS: 'TOOLS',
} as const;

export type FeatureKey = typeof FEATURE_KEYS[keyof typeof FEATURE_KEYS];

export const hasFeatureAccess = (
  requiredFeature: string | undefined,
  enabledFeatures?: string[]
) => {
  if (!requiredFeature) return true;
  if (enabledFeatures === undefined) return true;
  return enabledFeatures.some((feature) => feature.toUpperCase() === requiredFeature.toUpperCase());
};

export const getFeatureKeyForPath = (pathname: string): FeatureKey | undefined => {
  if (pathname === '/dashboard') return FEATURE_KEYS.DASHBOARD;
  if (pathname.startsWith('/reports')) return FEATURE_KEYS.REPORTS;
  if (pathname.startsWith('/employees') || pathname.startsWith('/admin/employee') || pathname.startsWith('/my-profile')) return FEATURE_KEYS.EMPLOYEES;
  if (pathname.startsWith('/attendance') || pathname.startsWith('/employee-punches')) return FEATURE_KEYS.ATTENDANCE;
  if (pathname.startsWith('/leave')) return FEATURE_KEYS.LEAVE;
  if (pathname.startsWith('/activity-logs')) return FEATURE_KEYS.ACTIVITY_LOGS;
  if (pathname.startsWith('/locations') || pathname.startsWith('/location-categories') || pathname.startsWith('/location-whitelist')) return FEATURE_KEYS.LOCATION;
  if (pathname.startsWith('/geo-location')) return FEATURE_KEYS.GEO_LOCATION;
  if (pathname.startsWith('/billing')) return FEATURE_KEYS.BILLING;
  if (pathname.startsWith('/notifications')) return FEATURE_KEYS.NOTIFICATIONS;
  if (pathname.startsWith('/payroll')) return FEATURE_KEYS.PAYROLL;
  if (pathname.startsWith('/bgv')) return FEATURE_KEYS.BACKGROUND_VERIFICATION;
  if (pathname.startsWith('/settings') || pathname.startsWith('/document-management-settings') || pathname.startsWith('/genral-default-setting')) return FEATURE_KEYS.SETTINGS;
  if (pathname.startsWith('/admin/announcements') || pathname.startsWith('/admin/org-setup')) return FEATURE_KEYS.TOOLS;
  return undefined;
};

export const isSuperAdminUser = (roles: string[]) =>
  roles.some((role) => normalizeRole(role) === 'superadmin');

export const isTenantAdminUser = (roles: string[]) =>
  roles.some((role) => {
    const normalizedRole = normalizeRole(role);
    return normalizedRole.includes('admin') && normalizedRole !== 'superadmin';
  });

const tenantAdminHiddenPayrollPermissions = [
  'ViewMySalary',
  'ViewMyPayslip',
  'ViewMyTaxDeclaration',
];

const tenantAdminOverridePermissions = [
  'ManageRoles',
  'ManagePermissions',
  'ViewPayroll',
  'ManagePayroll',
  'ProcessPayroll',
  'ViewBGV',
  'ManageBGV',
  'ViewGeoLocation',
  'ViewOwnGeoLocation',
];

export const userHasAnyAssignedPermission = (
  user: Pick<User, 'permissions' | 'roles'> | null | undefined
) => {
  const roles = user?.roles ?? [];
  return isSuperAdminUser(roles) || (user?.permissions?.length ?? 0) > 0;
};

export const hasPermissionAccess = (
  requiredPermission: string,
  permissions: string[] = [],
  roles: string[] = []
) => {
  if (requiredPermission === 'SuperAdmin' && isSuperAdminUser(roles)) {
    return true;
  }

  const hasAssignedPermissions = permissions.length > 0;
  const isTenantAdmin = hasAssignedPermissions && isTenantAdminUser(roles);

  if (isTenantAdmin && tenantAdminHiddenPayrollPermissions.includes(requiredPermission)) {
    return false;
  }

  if (permissions.includes(requiredPermission)) return true;

  if (isTenantAdmin && tenantAdminOverridePermissions.includes(requiredPermission)) {
    return true;
  }

  return false;
};

export const getDefaultAuthorizedRoute = (
  user: Pick<User, 'permissions' | 'roles' | 'roleName' | 'enabledFeatures'> | null | undefined
) => {
  if (!user) return '/login';

  const permissions = user.permissions ?? [];
  const roles = [...(user.roles ?? []), ...(user.roleName ? [user.roleName] : [])];
  const enabledFeatures = user.enabledFeatures;
  const canUseFeature = (feature: FeatureKey) => hasFeatureAccess(feature, enabledFeatures);
  const isTenantAdmin = isTenantAdminUser(roles);

  if (isSuperAdminUser(roles)) return '/super-admin/dashboard';
  if (!userHasAnyAssignedPermission(user)) return NO_PERMISSIONS_ROUTE;
  if (canUseFeature(FEATURE_KEYS.DASHBOARD) && (permissions.includes('ViewDashboard') || permissions.includes('ViewEmployeeDashboard'))) return '/dashboard';

  const canManageBgv = hasPermissionAccess('ManageBGV', permissions, roles);
  const canViewBgv = hasPermissionAccess('ViewBGV', permissions, roles);
  const canViewAssignedBgv = hasPermissionAccess('ViewAssignedBGV', permissions, roles);

  if (canUseFeature(FEATURE_KEYS.BACKGROUND_VERIFICATION) && (canManageBgv || canViewBgv)) return '/bgv/dashboard';
  if (canUseFeature(FEATURE_KEYS.BACKGROUND_VERIFICATION) && canViewAssignedBgv) return '/bgv/assigned-cases';
  if (canUseFeature(FEATURE_KEYS.REPORTS) && (permissions.includes('ViewReports') || permissions.includes('ViewOwnReports'))) return '/reports';
  if (canUseFeature(FEATURE_KEYS.EMPLOYEES) && permissions.includes('ViewEmployee')) return '/employees';
  if (canUseFeature(FEATURE_KEYS.EMPLOYEES) && permissions.includes('CreateEmployee')) return '/employees/upLoadBulkData';

  if (canUseFeature(FEATURE_KEYS.ATTENDANCE) && permissions.includes('ViewAttendanceRecords')) return '/attendance/records';
  if (canUseFeature(FEATURE_KEYS.ATTENDANCE) && permissions.includes('ViewAttendance')) return '/attendance';
  if (canUseFeature(FEATURE_KEYS.ATTENDANCE) && permissions.includes('ViewRegularizationRequests')) return '/attendance/regularization';
  if (canUseFeature(FEATURE_KEYS.ATTENDANCE) && permissions.includes('ViewAttendanceReports')) return '/attendance/report';
  if (canUseFeature(FEATURE_KEYS.ATTENDANCE) && permissions.includes('ManualOT')) return '/attendance/manual-ot';
  if (canUseFeature(FEATURE_KEYS.ATTENDANCE) && permissions.includes('ViewShifts')) return '/attendance/shift';
  if (canUseFeature(FEATURE_KEYS.ATTENDANCE) && permissions.includes('ManageShifts')) return '/attendance/rules';
  if (canUseFeature(FEATURE_KEYS.ATTENDANCE) && permissions.includes('ManageBiometric')) return '/attendance/biometric';

  if (canUseFeature(FEATURE_KEYS.LEAVE) && permissions.includes('ViewLeave') && !isTenantAdmin) return '/leave/my-leave';
  if (canUseFeature(FEATURE_KEYS.LEAVE) && (permissions.includes('ViewLeaveRequests') || permissions.includes('ApproveLeave'))) return '/leave/requests';
  if (canUseFeature(FEATURE_KEYS.LEAVE) && permissions.includes('ManageLeave')) return '/leave/assign';
  if (canUseFeature(FEATURE_KEYS.LEAVE) && permissions.includes('ViewLeaveUsageReport')) return '/leave/usage-report';
  if (canUseFeature(FEATURE_KEYS.ACTIVITY_LOGS) && permissions.includes('ViewActivityLogs')) return '/activity-logs/view-all-punches';
  if (canUseFeature(FEATURE_KEYS.LOCATION) && permissions.includes('ViewLocation')) return '/locations';
  if (canUseFeature(FEATURE_KEYS.LOCATION) && permissions.includes('ManageLocation')) return '/location-whitelist';
  if (canUseFeature(FEATURE_KEYS.GEO_LOCATION) && (hasPermissionAccess('ViewGeoLocation', permissions, roles) || hasPermissionAccess('ViewOwnGeoLocation', permissions, roles))) {
    return '/geo-location/history';
  }
  if (canUseFeature(FEATURE_KEYS.BILLING) && permissions.includes('ViewBilling')) return '/billing';
  if (canUseFeature(FEATURE_KEYS.NOTIFICATIONS) && permissions.includes('ViewNotifications')) return '/notifications';
  if (canUseFeature(FEATURE_KEYS.SETTINGS) && hasPermissionAccess('ManageRoles', permissions, roles)) return '/settings/roles';
  if (canUseFeature(FEATURE_KEYS.SETTINGS) && hasPermissionAccess('ManagePermissions', permissions, roles)) return '/settings/permissions';
  if (canUseFeature(FEATURE_KEYS.SETTINGS) && permissions.includes('ManageSettings')) return '/settings/account-management/organization';
  if (canUseFeature(FEATURE_KEYS.TOOLS) && permissions.includes('ManageTools')) return '/admin/announcements';

  const canViewPayroll =
    hasPermissionAccess('ViewPayroll', permissions, roles) ||
    hasPermissionAccess('ManagePayroll', permissions, roles) ||
    hasPermissionAccess('ProcessPayroll', permissions, roles);
  const canViewMySalary = hasPermissionAccess('ViewMySalary', permissions, roles);
  const canViewMyPayslip = hasPermissionAccess('ViewMyPayslip', permissions, roles);
  const canViewMyTaxDeclaration = hasPermissionAccess('ViewMyTaxDeclaration', permissions, roles);

  if (canUseFeature(FEATURE_KEYS.PAYROLL) && (canViewPayroll || canViewMySalary || canViewMyPayslip || canViewMyTaxDeclaration)) {
    if (canViewPayroll) return '/payroll/dashboard';
    if (canViewMySalary) return '/payroll/my-salary';
    if (canViewMyPayslip) return '/payroll/my-payslips';
    return '/payroll/my-tax-declarations';
  }

  return '/dashboard';
};

export const getPayrollDefaultRoute = (permissions: string[], roles: string[]) => {
  const canViewPayroll =
    hasPermissionAccess('ViewPayroll', permissions, roles) ||
    hasPermissionAccess('ManagePayroll', permissions, roles) ||
    hasPermissionAccess('ProcessPayroll', permissions, roles);

  if (canViewPayroll) return '/payroll/dashboard';
  if (hasPermissionAccess('ViewMySalary', permissions, roles)) return '/payroll/my-salary';
  if (hasPermissionAccess('ViewMyPayslip', permissions, roles)) return '/payroll/my-payslips';
  if (hasPermissionAccess('ViewMyTaxDeclaration', permissions, roles)) return '/payroll/my-tax-declarations';
  return '/dashboard';
};
